import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { api } from '../../context/AuthContext';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';

const QRCodeList = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQR, setSelectedQR] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Fetch QR codes on component mount
  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      console.log('Fetching QR codes...');
      
      const response = await api.get('/api/qrcodes');
      console.log('QR codes API response:', response.data);
      
      if (response.data.codes) {
        // Map the backend data format to what our component needs
        const formattedCodes = response.data.codes.map(code => ({
          _id: code._id,
          code: code.code,
          qrCodeUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${code.imageUrl}`,
          used: code.isAssigned,
          usedBy: code.memorialId ? 'Memorial' : '',
          createdAt: code.createdAt
        }));
        setQrCodes(formattedCodes);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Received invalid data format from server');
      }
    } catch (err) {
      console.error('Error fetching QR codes:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
      setError(`Failed to load QR codes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleQRCodeClick = (qrCode) => {
    setSelectedQR(qrCode);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handlePrint = () => {
    // Create a new window with just the QR code image
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code: ${selectedQR.code}</title>
          <style>
            body {
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              margin: 0;
              flex-direction: column;
              font-family: Arial, sans-serif;
            }
            .qr-container {
              text-align: center;
              padding: 20px;
            }
            .qr-code {
              width: 300px;
              height: 300px;
            }
            .qr-info {
              margin-top: 20px;
              font-size: 16px;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${selectedQR.qrCodeUrl}" alt="QR Code" class="qr-code"/>
            <div class="qr-info">
              <p><strong>Code:</strong> ${selectedQR.code}</p>
              <p><strong>Status:</strong> ${selectedQR.used ? 'Used' : 'Available'}</p>
              ${selectedQR.used ? `<p><strong>Used by:</strong> ${selectedQR.usedBy || 'Unknown'}</p>` : ''}
              <p><strong>Created:</strong> ${new Date(selectedQR.createdAt).toLocaleDateString()}</p>
            </div>
            <button class="no-print" onclick="window.print();return false;">Print QR Code</button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    // After a small delay to ensure the content is loaded
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = selectedQR.qrCodeUrl;
    link.download = `qrcode-${selectedQR.code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setNotification({
      open: true,
      message: 'QR Code downloaded successfully',
      severity: 'success'
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({
        open: true,
        message: 'URL copied to clipboard',
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to copy:', err);
      setNotification({
        open: true,
        message: 'Failed to copy URL',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Function to convert image URL to base64
  const getImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  // Bulk download functionality with images in Excel
  const handleBulkDownload = async () => {
    try {
      setBulkDownloading(true);
      setNotification({
        open: true,
        message: 'Preparing QR codes for download (this may take a moment)...',
        severity: 'info'
      });
      
      // Create a new Excel workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('QR Codes');
      
      // Define columns
      worksheet.columns = [
        { header: 'No.', key: 'no', width: 5 },
        { header: 'QR Code', key: 'image', width: 20 },
        { header: 'Code', key: 'code', width: 15 },
        { header: 'Status', key: 'status', width: 10 },
        { header: 'Used By', key: 'usedBy', width: 15 },
        { header: 'Created Date', key: 'createdDate', width: 15 },
        { header: 'Memorial URL', key: 'memorialUrl', width: 50 }
      ];
      
      // Style the header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      
      // Process each QR code and add it to the worksheet
      for (let i = 0; i < qrCodes.length; i++) {
        const qr = qrCodes[i];
        
        // Set row height to accommodate the image
        worksheet.getRow(i + 2).height = 120;
        
        // Get QR code image as base64
        const imageBase64 = await getImageAsBase64(qr.qrCodeUrl);
        
        if (imageBase64) {
          // Add the image to the workbook
          const imageId = workbook.addImage({
            base64: imageBase64,
            extension: 'png',
          });
          
          // Add the image to the cell
          worksheet.addImage(imageId, {
            tl: { col: 1, row: i + 1 },
            br: { col: 2, row: i + 2 },
            editAs: 'oneCell'
          });
        }
        
        // Add the data row
        worksheet.addRow({
          no: i + 1,
          // image cell is handled separately above
          code: qr.code,
          status: qr.used ? 'Used' : 'Available',
          usedBy: qr.usedBy || 'N/A',
          createdDate: new Date(qr.createdAt).toLocaleDateString(),
          memorialUrl: `${window.location.origin}/memorial/qr/${qr.code}`
        });
        
        // Center align the cells
        const row = worksheet.getRow(i + 2);
        row.alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell('memorialUrl').alignment = { vertical: 'middle', horizontal: 'left' };
      }
      
      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const excelBlob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(excelBlob, `QR_Codes_With_Images_${new Date().toISOString().slice(0,10)}.xlsx`);
      
      // Also download a ZIP file with all QR code images (for high-quality printing)
      await downloadAllQRImages();
      
      setNotification({
        open: true,
        message: 'QR codes exported successfully with images!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting QR codes with images:', error);
      setNotification({
        open: true,
        message: 'Failed to export QR codes: ' + error.message,
        severity: 'error'
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  // Function to download all QR code images as a ZIP file
  const downloadAllQRImages = async () => {
    try {
      // Dynamically import JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const imgFolder = zip.folder("qr_images");
      
      // Create a folder for the images
      const downloadPromises = qrCodes.map(async (qr) => {
        try {
          // Fetch each image
          const response = await fetch(qr.qrCodeUrl);
          const blob = await response.blob();
          
          // Add to zip with a descriptive filename
          imgFolder.file(`qrcode-${qr.code}.png`, blob);
          
          return true;
        } catch (err) {
          console.error(`Failed to download QR code ${qr.code}:`, err);
          return false;
        }
      });
      
      await Promise.all(downloadPromises);
      
      // Generate and download the zip file
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `QR_Codes_Images_${new Date().toISOString().slice(0,10)}.zip`);
      
      return true;
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        QR Code List
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ mb: { xs: 2, md: 0 } }}>
          This page lists all the QR codes in the system. Click on a QR code to view, print, or download it.
        </Typography>
        
        {qrCodes.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={bulkDownloading ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
          >
            {bulkDownloading ? 'Preparing...' : `Bulk Download (${qrCodes.length})`}
          </Button>
        )}
      </Box>
      
      {qrCodes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6">No QR codes found</Typography>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => window.location.href = '/admin/qrcodes/generate'}
          >
            Generate QR Codes
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {qrCodes.map((qrCode) => (
            <Grid item xs={12} sm={6} md={4} key={qrCode._id}>
              <Card 
                sx={{
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 3,
                  }
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleQRCodeClick(qrCode)}
                >
                  {qrCode.qrCodeUrl ? (
                    <Box 
                      component="img"
                      src={qrCode.qrCodeUrl}
                      alt={`QR Code ${qrCode.code}`}
                      sx={{ width: '100%', maxWidth: 200, height: 'auto' }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 200, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        bgcolor: 'grey.200'
                      }}
                    >
                      <QrCodeIcon sx={{ fontSize: 64, color: 'grey.500' }} />
                    </Box>
                  )}
                </Box>
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Code: {qrCode.code}
                  </Typography>
                  <Typography color={qrCode.used ? "error" : "success"}>
                    Status: {qrCode.used ? "Used" : "Available"}
                  </Typography>
                  {qrCode.used && (
                    <Typography variant="body2">
                      Used by: {qrCode.usedBy || "Unknown"}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(qrCode.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<QrCodeIcon />}
                    onClick={() => handleQRCodeClick(qrCode)}
                  >
                    View
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* QR Code Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedQR && (
          <>
            <DialogTitle>
              QR Code: {selectedQR.code}
            </DialogTitle>
            <DialogContent>
              <Box 
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2
                }}
              >
                <Box 
                  component="img"
                  src={selectedQR.qrCodeUrl}
                  alt={`QR Code ${selectedQR.code}`}
                  sx={{ width: '100%', maxWidth: 300, height: 'auto', mb: 2 }}
                />
                
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Code:</strong> {selectedQR.code}
                  </Typography>
                  <Typography variant="body1" gutterBottom color={selectedQR.used ? "error" : "success"}>
                    <strong>Status:</strong> {selectedQR.used ? "Used" : "Available"}
                  </Typography>
                  {selectedQR.used && (
                    <Typography variant="body1" gutterBottom>
                      <strong>Used by:</strong> {selectedQR.usedBy || "Unknown"}
                    </Typography>
                  )}
                  <Typography variant="body1" gutterBottom>
                    <strong>Created:</strong> {new Date(selectedQR.createdAt).toLocaleDateString()}
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      mt: 2,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1, mr: 2, wordBreak: 'break-all' }}>
                      {`${window.location.origin}/memorial/qr/${selectedQR.code}`}
                    </Typography>
                    <Tooltip title="Copy URL">
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => copyToClipboard(`${window.location.origin}/memorial/qr/${selectedQR.code}`)}
                      >
                        <FileCopyIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                variant="outlined" 
                onClick={handlePrint}
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
              <Button 
                variant="outlined"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
              <Button 
                variant="contained"
                onClick={handleCloseDialog}
                autoFocus
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Notification snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QRCodeList;